/**
 * Fade for the moment React replaces the prerendered fallback in #root.
 *
 * The fallback (scripts/prerender-fallback.js) is styled like the real page,
 * so the swap is mostly invisible; this softens what differs (data-driven
 * sections, the hero video) with a 150 ms opacity ramp from 0.9 to 1.
 * Content is never fully transparent, nothing is hidden before JavaScript
 * runs, and with prefers-reduced-motion nothing animates at all.
 *
 * The opacity/transition rules live in assets/styles/index.css under
 * `prefers-reduced-motion: no-preference`; this only toggles the classes.
 */

export const HANDOFF_START_CLASS = 'prerender-handoff';
export const HANDOFF_RUN_CLASS = 'prerender-handoff-run';
// Longer than the 150 ms transition, in case transitionend never fires.
const CLEANUP_MS = 400;

/** True when #root arrived with server-rendered fallback markup in it. */
export function hasPrerenderedContent(root: Element | null): boolean {
  return !!root && root.childElementCount > 0;
}

export function prefersReducedMotion(win: Window): boolean {
  try {
    return typeof win.matchMedia === 'function' && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Call right after React's first commit, before the browser paints it
 * (useLayoutEffect). Returns a cleanup that removes the classes.
 */
export function startHandoffFade(root: HTMLElement | null, win: Window = window): () => void {
  if (!root || prefersReducedMotion(win)) return () => {};

  let timer: number | undefined;
  const finish = () => {
    win.clearTimeout(timer);
    root.classList.remove(HANDOFF_START_CLASS, HANDOFF_RUN_CLASS);
    root.removeEventListener('transitionend', onEnd);
  };
  const onEnd = (e: Event) => {
    if (e.target === root) finish();
  };

  root.classList.add(HANDOFF_START_CLASS);
  // Force a style flush so the browser records the starting opacity; the
  // class swap below then transitions from it instead of jumping.
  void root.offsetWidth;
  root.classList.add(HANDOFF_RUN_CLASS);
  root.classList.remove(HANDOFF_START_CLASS);

  root.addEventListener('transitionend', onEnd);
  timer = win.setTimeout(finish, CLEANUP_MS);
  return finish;
}
